import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { balanceService, authService } from '../../services/api';
import AppAlert from '../../components/AppAlert';
import PaymentModal from '../payments/PaymentModal';
import './BalancePage.css';

/**
 * Página de saldo do grupo.
 * Mostra quanto cada membro pagou, deve e as transações necessárias.
 */
const BalancePage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState({
    isOpen: false,
    receiverId: null,
    receiverName: '',
    suggestedAmount: 0
  });

  const currentUser = authService.getCurrentUser();
  const currentUserId = Number(currentUser?.userId ?? currentUser?.id ?? 0) || null;

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    try {
      const data = await balanceService.calculate(groupId);
      setBalance(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value) || 0);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isCurrentMember = (member) => {
    if (!member || !currentUser) return false;

    const possibleIds = [
      member.userId,
      member.memberUserId,
      member.user?.id,
      member.user?.userId,
    ].filter(value => value !== undefined && value !== null);

    if (currentUserId && possibleIds.some(value => Number(value) === currentUserId)) {
      return true;
    }

    const possibleEmails = [
      member.email,
      member.userEmail,
      member.memberEmail,
      member.user?.email,
    ].filter(Boolean);

    if (currentUser?.email && possibleEmails.some(email => email === currentUser.email)) {
      return true;
    }

    return Boolean(currentUser?.name && member.memberName && currentUser.name === member.memberName);
  };

  const getMemberBalanceMeta = (balanceValue) => {
    if (balanceValue > 0) {
      return {
        tone: 'positive',
        badge: 'Recebe',
        amountPrefix: '+',
        amountLabel: 'tem a receber',
        explanation: `Pagou ${formatCurrency(Math.abs(balanceValue))} a mais que sua parte`,
        icon: 'fa-arrow-trend-up',
      };
    }

    if (balanceValue < 0) {
      return {
        tone: 'negative',
        badge: 'Paga',
        amountPrefix: '-',
        amountLabel: 'deve pagar',
        explanation: `Pagou ${formatCurrency(Math.abs(balanceValue))} a menos que sua parte`,
        icon: 'fa-arrow-trend-down',
      };
    }

    return {
      tone: 'neutral',
      badge: 'Quitado',
      amountPrefix: '',
      amountLabel: 'tudo certo',
      explanation: 'Já pagou exatamente sua parte',
      icon: 'fa-circle-check',
    };
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Calculando saldos...</p>
        </div>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><i className="fa-duotone fa-triangle-exclamation"></i></div>
          <h3 className="empty-state-title">Erro ao carregar saldo</h3>
          <button onClick={loadBalance} className="btn btn-primary">
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  const memberBalances = [...(balance.memberBalances || [])];
  const transactions = [...(balance.transactions || [])];
  const allSettled = transactions.length === 0;

  const sortedMemberBalances = memberBalances.sort((a, b) => {
    const aIsCurrent = isCurrentMember(a) ? 1 : 0;
    const bIsCurrent = isCurrentMember(b) ? 1 : 0;
    if (aIsCurrent !== bIsCurrent) return bIsCurrent - aIsCurrent;

    const aValue = Number(a.balance) || 0;
    const bValue = Number(b.balance) || 0;
    const aRank = aValue > 0 ? 0 : aValue < 0 ? 1 : 2;
    const bRank = bValue > 0 ? 0 : bValue < 0 ? 1 : 2;
    if (aRank !== bRank) return aRank - bRank;

    return Math.abs(bValue) - Math.abs(aValue);
  });

  const totalToReceive = sortedMemberBalances.reduce((sum, member) => {
    const value = Number(member.balance) || 0;
    return value > 0 ? sum + value : sum;
  }, 0);

  const totalToPay = Math.abs(sortedMemberBalances.reduce((sum, member) => {
    const value = Number(member.balance) || 0;
    return value < 0 ? sum + value : sum;
  }, 0));

  const totalSettlementAmount = transactions.reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0);

  const myTransactions = currentUserId
    ? transactions.filter(transaction =>
        Number(transaction.fromUserId) === currentUserId || Number(transaction.toUserId) === currentUserId
      )
    : [];

  const myPendingPayments = currentUserId
    ? transactions.filter(transaction => Number(transaction.fromUserId) === currentUserId)
    : [];

  const myPendingReceipts = currentUserId
    ? transactions.filter(transaction => Number(transaction.toUserId) === currentUserId)
    : [];

  const currentMemberBalance = sortedMemberBalances.find(isCurrentMember);
  const currentMemberBalanceValue = Number(currentMemberBalance?.balance) || 0;
  const currentMemberMeta = currentMemberBalance ? getMemberBalanceMeta(currentMemberBalanceValue) : null;

  const largestTransaction = transactions.reduce((largest, transaction) => {
    const amount = Number(transaction.amount) || 0;
    return amount > largest ? amount : largest;
  }, 0);

  const orderedTransactions = [...transactions].sort((a, b) => {
    const aMine = Number(a.fromUserId) === currentUserId || Number(a.toUserId) === currentUserId ? 1 : 0;
    const bMine = Number(b.fromUserId) === currentUserId || Number(b.toUserId) === currentUserId ? 1 : 0;
    if (aMine !== bMine) return bMine - aMine;
    return (Number(b.amount) || 0) - (Number(a.amount) || 0);
  });

  return (
    <div className="page-container balance-page">
      <section className="balance-hero">
        <Link to={`/groups/${groupId}`} className="balance-backlink">
          <i className="fa-solid fa-arrow-left"></i>
          Voltar para o grupo
        </Link>

        <div className="balance-hero__title-row">
          <div className="balance-hero__icon" aria-hidden="true">
            <i className="fa-duotone fa-wallet"></i>
          </div>
          <div className="balance-hero__title-block">
            <span className="balance-hero__eyebrow">Entendimento rápido</span>
            <h1 className="balance-hero__title">Saldo do Grupo</h1>
          </div>
        </div>

        <p className="balance-hero__description">
          Veja quem tem valores a receber, quem precisa pagar e quais transferências resolvem o grupo com o menor número de pagamentos.
        </p>

        <div className="balance-hero__chips">
          <span className="balance-hero__chip">
            <i className="fa-solid fa-user-group"></i>
            {sortedMemberBalances.length} participante{sortedMemberBalances.length !== 1 ? 's' : ''}
          </span>
          <span className="balance-hero__chip">
            <i className="fa-solid fa-arrow-right-arrow-left"></i>
            {transactions.length} transaç{transactions.length === 1 ? 'ão' : 'ões'} para acertar
          </span>
          <span className="balance-hero__chip">
            <i className="fa-solid fa-scale-balanced"></i>
            Volume: {formatCurrency(totalSettlementAmount)}
          </span>
        </div>
      </section>

      {allSettled ? (
        <section className="balance-settled-banner animate-fadeIn">
          <div className="balance-settled-banner__icon" aria-hidden="true">
            <i className="fa-duotone fa-party-horn"></i>
          </div>
          <div>
            <h2 className="balance-settled-banner__title">Todas as contas estão acertadas</h2>
            <p className="balance-settled-banner__text">Ninguém deve nada a ninguém neste momento.</p>
          </div>
        </section>
      ) : (
        <AppAlert
          variant="info"
          title="Como usar esta tela"
          className="balance-help-alert animate-fadeIn"
          style={{ marginBottom: 18 }}
        >
          Siga a lista de transações abaixo. Elas já mostram a forma mais simples de quitar o grupo com menos transferências.
        </AppAlert>
      )}

      <section className="balance-metrics-grid" aria-label="Resumo do saldo">
        <div className="balance-metric-card">
          <div className="balance-metric-card__icon balance-metric-card__icon--success">
            <i className="fa-solid fa-hand-holding-dollar"></i>
          </div>
          <div className="balance-metric-card__content">
            <span className="balance-metric-card__label">Total a receber</span>
            <strong className="balance-metric-card__value">{formatCurrency(totalToReceive)}</strong>
            <small className="balance-metric-card__hint">
              Soma dos membros com saldo positivo
            </small>
          </div>
        </div>

        <div className="balance-metric-card">
          <div className="balance-metric-card__icon balance-metric-card__icon--danger">
            <i className="fa-solid fa-credit-card"></i>
          </div>
          <div className="balance-metric-card__content">
            <span className="balance-metric-card__label">Total a pagar</span>
            <strong className="balance-metric-card__value">{formatCurrency(totalToPay)}</strong>
            <small className="balance-metric-card__hint">
              Soma dos membros com saldo negativo
            </small>
          </div>
        </div>

        <div className="balance-metric-card">
          <div className="balance-metric-card__icon balance-metric-card__icon--primary">
            <i className="fa-solid fa-list-check"></i>
          </div>
          <div className="balance-metric-card__content">
            <span className="balance-metric-card__label">Transações sugeridas</span>
            <strong className="balance-metric-card__value">{transactions.length}</strong>
            <small className="balance-metric-card__hint">
              Maior pagamento: {formatCurrency(largestTransaction)}
            </small>
          </div>
        </div>

        <div className="balance-metric-card">
          <div className="balance-metric-card__icon balance-metric-card__icon--secondary">
            <i className="fa-solid fa-user"></i>
          </div>
          <div className="balance-metric-card__content">
            <span className="balance-metric-card__label">Sua participação</span>
            <strong className="balance-metric-card__value">{myTransactions.length}</strong>
            <small className="balance-metric-card__hint">
              {myPendingPayments.length} para pagar • {myPendingReceipts.length} para receber
            </small>
          </div>
        </div>
      </section>

      {currentMemberBalance && (
        <section className={`balance-user-spotlight balance-user-spotlight--${currentMemberMeta.tone} animate-fadeIn`}>
          <div className="balance-user-spotlight__avatar">
            {getInitials(currentMemberBalance.memberName || currentUser?.name || 'Você')}
          </div>
          <div className="balance-user-spotlight__content">
            <span className="balance-user-spotlight__eyebrow">Seu status no grupo</span>
            <h2 className="balance-user-spotlight__title">
              {currentMemberMeta.tone === 'positive' && 'Você tem valor a receber'}
              {currentMemberMeta.tone === 'negative' && 'Você precisa pagar'}
              {currentMemberMeta.tone === 'neutral' && 'Você está com saldo zerado'}
            </h2>
            <p className="balance-user-spotlight__description">{currentMemberMeta.explanation}</p>
          </div>
          <div className="balance-user-spotlight__amount">
            <span className={`balance-user-spotlight__badge balance-user-spotlight__badge--${currentMemberMeta.tone}`}>
              {currentMemberMeta.badge}
            </span>
            <strong>
              {currentMemberMeta.amountPrefix}
              {formatCurrency(Math.abs(currentMemberBalanceValue))}
            </strong>
          </div>
        </section>
      )}

      <section className="balance-section-card animate-fadeIn">
        <div className="balance-section-card__header">
          <div>
            <h2 className="balance-section-card__title">Resumo por membro</h2>
            <p className="balance-section-card__subtitle">
              Positivo = tem a receber. Negativo = precisa pagar. Zero = já está acertado.
            </p>
          </div>
        </div>

        {sortedMemberBalances.length === 0 ? (
          <div className="balance-empty-inline">
            <i className="fa-solid fa-users"></i>
            Nenhum saldo disponível para este grupo ainda.
          </div>
        ) : (
          <div className="balance-member-list">
            {sortedMemberBalances.map((member, index) => {
              const balanceValue = Number(member.balance) || 0;
              const meta = getMemberBalanceMeta(balanceValue);
              const isMine = isCurrentMember(member);

              return (
                <div
                  key={member.memberId || `${member.memberName}-${index}`}
                  className={`balance-member-row balance-member-row--${meta.tone} ${isMine ? 'is-current-user' : ''} animate-fadeIn`}
                  style={{ animationDelay: `${index * 0.04}s` }}
                >
                  <div className="balance-member-row__main">
                    <div className={`balance-member-row__avatar balance-member-row__avatar--${meta.tone}`}>
                      {getInitials(member.memberName)}
                    </div>

                    <div className="balance-member-row__info">
                      <div className="balance-member-row__name">
                        {member.memberName}
                        {isMine && <span className="balance-member-row__tag">VOCÊ</span>}
                      </div>
                      <div className="balance-member-row__description">{meta.explanation}</div>
                    </div>
                  </div>

                  <div className="balance-member-row__summary">
                    <span className={`balance-member-row__badge balance-member-row__badge--${meta.tone}`}>
                      <i className={`fa-solid ${meta.icon}`}></i>
                      {meta.badge}
                    </span>
                    <div className={`balance-member-row__amount balance-member-row__amount--${meta.tone}`}>
                      {meta.amountPrefix}
                      {formatCurrency(Math.abs(balanceValue))}
                    </div>
                    <div className="balance-member-row__amount-label">{meta.amountLabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="balance-section-card animate-fadeIn" style={{ animationDelay: '0.1s' }}>
        <div className="balance-section-card__header">
          <div>
            <h2 className="balance-section-card__title">Transações para acertar</h2>
            <p className="balance-section-card__subtitle">
              Faça apenas estas transferências para zerar o saldo do grupo.
            </p>
          </div>
          {!allSettled && (
            <div className="balance-section-card__meta">
              <span>Volume total</span>
              <strong>{formatCurrency(totalSettlementAmount)}</strong>
            </div>
          )}
        </div>

        {allSettled ? (
          <div className="balance-empty-inline balance-empty-inline--success">
            <i className="fa-solid fa-circle-check"></i>
            Nenhuma transação pendente. O grupo já está equilibrado.
          </div>
        ) : (
          <div className="balance-transaction-list">
            {orderedTransactions.map((transaction, index) => {
              const amount = Number(transaction.amount) || 0;
              const isCurrentUserDebtor = Number(transaction.fromUserId) === currentUserId;
              const isCurrentUserReceiver = Number(transaction.toUserId) === currentUserId;
              const tone = isCurrentUserDebtor ? 'debt' : isCurrentUserReceiver ? 'credit' : 'neutral';

              return (
                <div
                  key={`${transaction.fromUserId}-${transaction.toUserId}-${transaction.amount}-${index}`}
                  className={`balance-transaction-card balance-transaction-card--${tone} animate-fadeIn`}
                  style={{ animationDelay: `${0.12 + index * 0.05}s` }}
                >
                  <div className="balance-transaction-card__top">
                    <span className={`balance-transaction-card__badge balance-transaction-card__badge--${tone}`}>
                      {isCurrentUserDebtor && 'Você paga'}
                      {isCurrentUserReceiver && 'Você recebe'}
                      {!isCurrentUserDebtor && !isCurrentUserReceiver && 'Entre membros'}
                    </span>
                    <div className="balance-transaction-card__amount">{formatCurrency(amount)}</div>
                  </div>

                  <div className="balance-transaction-card__sentence">
                    {isCurrentUserDebtor ? (
                      <>Você deve pagar <strong>{formatCurrency(amount)}</strong> para <strong>{transaction.toMemberName}</strong>.</>
                    ) : isCurrentUserReceiver ? (
                      <><strong>{transaction.fromMemberName}</strong> deve pagar <strong>{formatCurrency(amount)}</strong> para você.</>
                    ) : (
                      <><strong>{transaction.fromMemberName}</strong> deve pagar <strong>{formatCurrency(amount)}</strong> para <strong>{transaction.toMemberName}</strong>.</>
                    )}
                  </div>

                  <div className="balance-transaction-card__flow">
                    <div className="balance-transaction-party">
                      <div className="balance-transaction-party__avatar balance-transaction-party__avatar--debt">
                        {getInitials(transaction.fromMemberName)}
                      </div>
                      <div className="balance-transaction-party__label">
                        <span className="balance-transaction-party__role">Quem paga</span>
                        <strong>{transaction.fromMemberName}</strong>
                      </div>
                    </div>

                    <div className="balance-transaction-card__arrow" aria-hidden="true">
                      <i className="fa-solid fa-arrow-right"></i>
                    </div>

                    <div className="balance-transaction-party balance-transaction-party--right">
                      <div className="balance-transaction-party__label balance-transaction-party__label--right">
                        <span className="balance-transaction-party__role">Quem recebe</span>
                        <strong>{transaction.toMemberName}</strong>
                      </div>
                      <div className="balance-transaction-party__avatar balance-transaction-party__avatar--credit">
                        {getInitials(transaction.toMemberName)}
                      </div>
                    </div>
                  </div>

                  {isCurrentUserDebtor && (
                    <div className="balance-transaction-card__actions">
                      <button
                        onClick={() => setPaymentModal({
                          isOpen: true,
                          receiverId: transaction.toUserId,
                          receiverName: transaction.toMemberName,
                          suggestedAmount: amount
                        })}
                        className="btn btn-primary balance-transaction-card__pay-btn"
                      >
                        <i className="fa-duotone fa-qrcode"></i>
                        Pagar com PIX
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <div className="balance-page__footer">
        <button
          onClick={() => navigate(`/groups/${groupId}`)}
          className="btn btn-secondary"
        >
          <i className="fa-solid fa-arrow-left"></i>
          Voltar ao Grupo
        </button>
      </div>

      <PaymentModal
        isOpen={paymentModal.isOpen}
        onClose={() => setPaymentModal({ ...paymentModal, isOpen: false })}
        groupId={parseInt(groupId, 10)}
        receiverId={paymentModal.receiverId}
        receiverName={paymentModal.receiverName}
        suggestedAmount={paymentModal.suggestedAmount}
      />
    </div>
  );
};

export default BalancePage;
